
import React, { useState, useEffect } from 'react';
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { 
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

// Form validation schema
const providerFormSchema = z.object({
  name: z.string().min(3, "Name must be at least 3 characters"),
  url: z.string().url("Please enter a valid URL").or(z.string().length(0)), 
  description: z.string().optional(),
  active: z.boolean().default(true),
});

type ProviderFormValues = z.infer<typeof providerFormSchema>;

interface ProviderFormProps {
  onSubmit: (data: any, isValid: boolean) => void;
  initialData?: any;
}

export const ProviderForm = ({ onSubmit, initialData }: ProviderFormProps) => {
  // Initialize form with default values or initial data
  const form = useForm<ProviderFormValues>({
    resolver: zodResolver(providerFormSchema),
    defaultValues: {
      name: initialData?.name || "",
      url: initialData?.url || "",
      description: initialData?.description || "",
      active: initialData?.active !== undefined ? initialData.active : true,
    },
    mode: "all", // Changed from onChange to all to validate all fields more aggressively
  });

  // Check if form is valid and update parent
  const formState = form.formState;
  
  useEffect(() => {
    // Check if the form has been interacted with
    const isTouched = Object.keys(formState.touchedFields).length > 0;
    
    if (isTouched || initialData) {
      const data = form.getValues();
      // Pass the actual form validity state instead of always true
      onSubmit({
        provider: {
          name: data.name,
          url: data.url,
          active: data.active,
          description: data.description
        }
      }, formState.isValid);
    }
  }, [formState.isValid, formState.isDirty, form, onSubmit, initialData]);
  
  const handleFormSubmit = (data: ProviderFormValues) => {
    // Pass the actual validity state when submitting the form
    onSubmit({
      provider: {
        name: data.name,
        url: data.url,
        active: data.active,
        description: data.description
      }
    }, true);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6">
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Provider Name <span className="text-red-500">*</span></FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Enter provider name"
                    className={cn(
                      formState.errors.name && "border-red-500 focus-visible:ring-red-500"
                    )}
                  />
                </FormControl>
                <FormDescription>
                  The name of the trading provider that will appear to users
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="url"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Website URL</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="url"
                    placeholder="https://example.com"
                  />
                </FormControl>
                <FormDescription>
                  The provider's official website (optional)
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder="Enter a description of this provider"
                    rows={4}
                  />
                </FormControl>
                <FormDescription>
                  Provide details about the trading provider and their expertise
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="active"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">Active Status</FormLabel>
                  <FormDescription>
                    When active, this provider will be available in the system
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        
        {/* Adding a hidden submit button to enable form submission */}
        <button type="submit" className="hidden" aria-hidden="true"></button>
      </form>
    </Form>
  );
};
